import {
  Button,
  Center,
  Flex,
  FormLabel,
  HStack,
  Link,
  Spinner,
  Td,
  Text,
  Tr,
  useToast,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import doCapitalize from "lodash/capitalize";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  IncompleteBarCells,
  IncompleteBarPattern,
} from "@/common/charts/components/IncompleteBar";
import useChartTokens from "@/common/charts/hooks/useChartTokens";
import CompactNumber from "@/common/components/CompactNumber";
import SimpleTable from "@/common/components/SimpleTable";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { API_URL } from "@/common/utils/env";
import { locale } from "@/common/utils/locale";
import { formatLargeNumber } from "@/common/utils/numberFormatting";
import { TrialStatus } from "@/global-settings/components/TrialStatus";
import { useBillingUsage } from "@/global-settings/data/useBillingUsage";
import { useCheckoutSession } from "@/global-settings/data/useCheckoutSession";
import { BillingPortalUrl, GlobalSettingsUrl } from "@reflag/shared/urls";

const monthDateFormatter = new Intl.DateTimeFormat(locale, {
  year: "numeric",
  month: "long",
});

const shortMonthFormatter = new Intl.DateTimeFormat(locale, {
  year: "2-digit",
  month: "short",
});

function isCurrentMonth(date: Date): boolean {
  return dayjs().isSame(date, "month");
}

function zeroFillYear(
  actualUsage: Array<{ month: Date; trackedUsers: number }>,
) {
  const result = new Array(12).fill(0).map((_, idx, arr) => {
    return {
      month: dayjs()
        .startOf("month")
        .subtract(arr.length - idx, "month")
        .toDate(),
      trackedUsers: 0,
    };
  });

  result.splice(-actualUsage.length, actualUsage.length, ...actualUsage);

  return result;
}

function ManageSubscription({ orgId }: { orgId: string }) {
  const { org } = useEnvironment();

  if (!org.hasStripeCustomerId) {
    return (
      <Text>
        Contact sales at{" "}
        <Link href="mailto:sales@reflag.com">sales@reflag.com</Link> to manage
        your subscription.
      </Text>
    );
  }

  return (
    <Button
      as={Link}
      href={BillingPortalUrl(API_URL, orgId)}
      size="sm"
      variant="outline"
      w="fit-content"
    >
      Manage your subscription
    </Button>
  );
}

function ProPlanCheckoutButton({
  paymentTerms,
}: {
  paymentTerms: "monthly" | "yearly";
}) {
  const { env } = useEnvironment();
  const toast = useToast();
  const checkoutSession = useCheckoutSession();

  const handleProPlanCheckout = async () => {
    try {
      const successUrl = `${window.location.origin}${GlobalSettingsUrl(env, "org-billing")}?success=true`;
      const cancelUrl = `${window.location.origin}${GlobalSettingsUrl(env, "org-billing")}?canceled=true`;

      const result = await checkoutSession.mutateAsync({
        successUrl,
        cancelUrl,
        paymentTerms,
      });

      // Redirect to Stripe checkout
      window.location.href = result.sessionUrl;
    } catch {
      toast({
        title: "Failed to create checkout session. Please try again.",
        status: "error",
      });
    }
  };

  return (
    <Button
      isLoading={checkoutSession.isPending}
      loadingText="Preparing..."
      size="sm"
      variant="solid"
      w="fit-content"
      onClick={handleProPlanCheckout}
    >
      Upgrade to Pro ({paymentTerms})
    </Button>
  );
}

function ProPlanCheckout() {
  return (
    <Flex direction="column" gap="3">
      <Text color="dimmed">
        Subscribe to a plan to unlock advanced features and higher usage limits.
        See the <Link href="https://reflag.com/pricing">pricing page</Link> for
        details. Contact{" "}
        <Link href="mailto:sales@reflag.com">sales@reflag.com</Link> for
        enterprise plans.
      </Text>
      <HStack gap="3">
        <ProPlanCheckoutButton paymentTerms="monthly" />
        <ProPlanCheckoutButton paymentTerms="yearly" />
      </HStack>
    </Flex>
  );
}

export default function OrganizationBilling() {
  const { org } = useEnvironment();
  const { colors, fontSizes, radii } = useChartTokens();
  const billingUsage = useBillingUsage();
  const toast = useToast();

  const trackingData = (billingUsage.data || []).slice(0, 12).reverse();
  const graphData = zeroFillYear(trackingData);

  // Handle checkout success/error messages
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const canceled = urlParams.get("canceled");

    if (success === "true") {
      toast({
        title: "Your Pro plan subscription has been activated.",
        status: "success",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === "true") {
      toast({
        description: "Your checkout was canceled. You can try again anytime.",
        status: "info",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  return (
    <Flex direction={"column"} fontSize="sm" gap="5" maxWidth="wideForm">
      <Flex direction={"column"} gap="3">
        <FormLabel pb={0}>Payment plan</FormLabel>
        {org.trialEndsAt !== null ? (
          <>
            <Text color="dimmed">
              You are currently on a &apos;Pro&apos; plan trial. You will
              automatically be downgraded to the free &apos;starter&apos; plan
              when the trial ends.
            </Text>
            <TrialStatus w={"md"} />
            <ProPlanCheckout />
          </>
        ) : (
          <>
            <Text color="dimmed">
              You are currently on the{" "}
              <Text as="strong" color="chakra-body-text">
                {doCapitalize(org.accessLevel)}
              </Text>
              &nbsp;plan.
            </Text>
            {org.accessLevel === "starter" ? (
              <ProPlanCheckout />
            ) : (
              <ManageSubscription orgId={org.id} />
            )}
          </>
        )}
      </Flex>

      <Flex direction={"column"} gap="3">
        <FormLabel pb={0}>Monthly tracked users</FormLabel>

        <Text maxW="lg" size="sm" variant="dimmed">
          Count of Monthly Tracked Users (MTU) per calendar month. Users are
          tracked across all apps in the organization.
        </Text>

        <ResponsiveContainer height={300} width="100%">
          {billingUsage.isLoading ? (
            <Center>
              <Spinner />
            </Center>
          ) : (
            <BarChart
              data={graphData}
              margin={{
                top: 10, //why is this necessary to match axis labels
                right: 10, //why is this necessary to match axis labels
                bottom: 0,
                left: 0,
              }}
            >
              <CartesianGrid
                stroke={colors.grid}
                strokeDasharray="4"
                vertical={false}
                yAxisId="left"
              />
              <ReferenceLine stroke={colors.grid} y={0} yAxisId="left" />
              <XAxis
                axisLine={false}
                dataKey="month"
                dy={8}
                fontSize={fontSizes.axisLabel}
                height={50}
                stroke={colors.axis}
                tickFormatter={(val) =>
                  val instanceof Date ? shortMonthFormatter.format(val) : ""
                }
                tickLine={true}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                fontSize={fontSizes.axisLabel}
                stroke={colors.axis}
                tick={{
                  dx: 4,
                }}
                tickFormatter={formatLargeNumber}
                tickLine={false}
                type="number"
                width={50}
                yAxisId="left"
              />
              <Tooltip
                contentStyle={{
                  borderColor: colors.tooltipBorder,
                  backgroundColor: colors.tooltipBg,
                  borderRadius: radii.tooltip,
                  fontSize: fontSizes.tooltip,
                }}
                isAnimationActive={false}
                labelFormatter={(val) =>
                  val instanceof Date ? monthDateFormatter.format(val) : ""
                }
              />
              {!billingUsage.isLoading && (
                <>
                  <defs>
                    <IncompleteBarPattern
                      colorName={colors.primary}
                      colorValue={colors.primary}
                    />
                  </defs>
                  <Bar
                    key="trackedUsers"
                    animationDuration={300}
                    dataKey="trackedUsers"
                    fill={colors.primary}
                    name="Tracked users"
                    radius={[7, 7, 0, 0]}
                    stackId="a"
                    yAxisId="left"
                  >
                    {IncompleteBarCells({
                      data: graphData,
                      color: colors.primary,
                    })}
                  </Bar>
                </>
              )}
            </BarChart>
          )}
        </ResponsiveContainer>

        <SimpleTable
          columns={["Month", "Users"]}
          rows={billingUsage.data || []}
          rowTemplate={(d) => (
            <Tr key={monthDateFormatter.format(new Date(d.month))}>
              <Td>
                <HStack>
                  <Text as="span">
                    {monthDateFormatter.format(new Date(d.month))}
                  </Text>
                  {isCurrentMonth(d.month) && (
                    <Text as="span" variant={"dimmed"}>
                      (current)
                    </Text>
                  )}
                </HStack>
              </Td>
              <Td>
                <CompactNumber value={d.trackedUsers} />
              </Td>
            </Tr>
          )}
        ></SimpleTable>
      </Flex>
    </Flex>
  );
}
